// Classificação de conversa (MET-15/17) — os dois PATCH que vivem no módulo
// metrics, mas cujo path é `/v1/conversations/:id/*`.
//
// O contrato §6.10 os declara aqui de propósito: nenhuma das duas rotas existe em
// `conversations.controller.ts`, então o módulo `conversations` não precisa ser
// editado — menos superfície de conflito no código mais quente do produto.
//
// Invalidam `Conversation` (a tela do inbox mostra o valor novo) E
// `MetricsAggregate` (o funil e a distribuição por origem mudam com a correção).
//
// ⚠️ Este arquivo foi escrito contra MOCK meu, antes de as rotas existirem, e
// tinha QUATRO divergências do contrato publicado — a pior delas (`stageId` em vez
// de `funnelStageId`) devolveria 400 em toda chamada. Nenhuma apareceu em teste,
// porque eu havia escrito os dois lados. Os campos abaixo saem do
// `SetLeadSourceDto`/`SetFunnelStageDto` do OpenAPI da API viva, não de memória.
import { api } from '@/lib/api';
import type { LeadSourceOrigin } from '../model';

export interface SetLeadSourceArgs {
  conversationId: string;
  /**
   * `null` é ESCOLHA, não campo vazio: o atendente declarando "sem origem".
   * O contrato marca o campo como `required` **e** `nullable`, então ele vai
   * sempre no corpo — omiti-lo não é a mesma coisa que mandar `null`, e sem o
   * `null` explícito a detecção automática segue tentando preencher para sempre.
   */
  leadSourceId: string | null;
  /** Lock otimista. Opcional no contrato; mandamos sempre que a tela souber. */
  version?: number;
}

export interface SetStageArgs {
  conversationId: string;
  /** ⚠️ `funnelStageId` — o nome `stageId` dá 400 de validação. */
  funnelStageId: string;
  version?: number;
}

/**
 * As duas rotas devolvem a CONVERSA atualizada, com `version` novo — não um eco
 * do campo escrito. O tipo aqui é o mínimo que a UI consome; o detalhe completo
 * chega pelo refetch que o `invalidatesTags` dispara.
 */
export interface ClassifiedConversation {
  id: string;
  version: number;
  leadSourceId?: string | null;
  leadSourceOrigin?: LeadSourceOrigin;
  funnelStageId?: string | null;
}

export const metricsConversationApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * MET-17. Grava `leadSourceOrigin='MANUAL'`, e é isso que faz a detecção
     * automática NUNCA sobrescrever a escolha do atendente depois.
     */
    metricsSetLeadSource: build.mutation<ClassifiedConversation, SetLeadSourceArgs>({
      query: ({ conversationId, leadSourceId, version }) => ({
        url: `/conversations/${conversationId}/lead-source`,
        method: 'PATCH',
        // `leadSourceId` entra SEMPRE (inclusive `null`); `version` só quando há.
        body: { leadSourceId, ...(version !== undefined ? { version } : {}) },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Conversation' as const, id: arg.conversationId },
        'MetricsAggregate',
      ],
    }),
    /**
     * MET-15. O back fecha a transição anterior (`exitedAt` + `dwellSec`) na
     * MESMA transação — é isso que sustenta "tempo médio de permanência por
     * etapa". Mover para a etapa em que a conversa já está é no-op e NÃO
     * incrementa `version`: reabrir a mesma etapa zeraria a permanência
     * acumulada e mostraria "entrou agora" numa conversa parada há três dias.
     */
    metricsSetStage: build.mutation<ClassifiedConversation, SetStageArgs>({
      query: ({ conversationId, funnelStageId, version }) => ({
        url: `/conversations/${conversationId}/stage`,
        method: 'PATCH',
        body: { funnelStageId, ...(version !== undefined ? { version } : {}) },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Conversation' as const, id: arg.conversationId },
        // A etapa é COLUNA no Pipeline, que provê a tag `QUEUE`. Sem isto,
        // classificar pelo painel do inbox deixava o quadro na coluna velha até
        // o TTL de 15s — o mesmo defeito que o arraste já corrigiu do outro lado.
        { type: 'Conversation' as const, id: 'QUEUE' },
        'MetricsAggregate',
      ],
    }),
  }),
  overrideExisting: false,
});

export const { useMetricsSetLeadSourceMutation, useMetricsSetStageMutation } =
  metricsConversationApi;
